import { zodResolver } from '@hookform/resolvers/zod';
import { type Role, type UpdateUserInput, updateUserInputSchema } from '@idpe/schema';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';

import { Badge } from '~/components/ui/Badge.js';
import { Button } from '~/components/ui/Button.js';
import { Field } from '~/components/ui/Field.js';
import { ConfirmDialog } from '~/components/ui/Modal.js';
import { Select } from '~/components/ui/Select.js';
import { Spinner } from '~/components/ui/Spinner.js';
import { Switch } from '~/components/ui/Switch.js';
import { useAuth } from '~/features/auth/hooks.js';
import { UserResetPasswordDialog } from '~/features/users/UserResetPasswordDialog.js';
import { useDeactivateUser, useUpdateUser, useUser } from '~/features/users/hooks.js';
import { ApiError } from '~/lib/api.js';

const ROLE_OPTIONS: { value: Role; label: string }[] = [
  { value: 'editor', label: 'Editor' },
  { value: 'revisor', label: 'Revisor' },
  { value: 'publisher', label: 'Publisher' },
  { value: 'admin', label: 'Admin' },
];

export const Route = createFileRoute('/_auth/users/$id')({
  component: UserEditPage,
});

function UserEditPage() {
  const { id } = Route.useParams();
  const navigate = Route.useNavigate();
  const { user: me } = useAuth();
  const { data: user, isLoading, error } = useUser(id);
  const update = useUpdateUser(id);
  const deactivate = useDeactivateUser(id);
  const [resetOpen, setResetOpen] = useState(false);
  const [deactivateOpen, setDeactivateOpen] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isDirty, isSubmitting },
  } = useForm<UpdateUserInput>({
    resolver: zodResolver(updateUserInputSchema),
    values: user ? { nome: user.nome, papel: user.papel, ativo: user.ativo } : undefined,
  });

  if (isLoading) {
    return (
      <div className="page">
        <div className="dt__state">
          <Spinner size="lg" />
        </div>
      </div>
    );
  }

  if (error || !user) {
    return (
      <div className="page">
        <header className="page-head">
          <p className="eyebrow">/ Erro</p>
          <h1 className="page-title">Usuário não encontrado.</h1>
        </header>
        <Link to="/users">
          <Button variant="secondary">Voltar à lista</Button>
        </Link>
      </div>
    );
  }

  const isSelf = me?.id === user.id;

  async function onSubmit(values: UpdateUserInput) {
    try {
      await update.mutateAsync(values);
      toast.success('Alterações salvas.');
      reset(values);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.displayMessage : 'Erro ao atualizar usuário.');
    }
  }

  async function handleDeactivate() {
    try {
      await deactivate.mutateAsync();
      toast.success('Usuário desativado e sessões encerradas.');
      setDeactivateOpen(false);
      navigate({ to: '/users' });
    } catch (err) {
      toast.error(err instanceof ApiError ? err.displayMessage : 'Erro ao desativar.');
    }
  }

  return (
    <div className="page">
      <header className="page-head">
        <p className="eyebrow">/ Equipe</p>
        <h1 className="page-title">{user.nome}</h1>
        <p className="page-sub">
          <span style={{ color: 'var(--fg-muted)' }}>{user.email}</span> ·{' '}
          {user.ativo ? (
            <Badge tone="success">ativo</Badge>
          ) : (
            <Badge tone="muted">desativado</Badge>
          )}
        </p>
      </header>

      <section className="form-card">
        <h2 className="form-card__title">Dados do usuário</h2>

        <form onSubmit={handleSubmit(onSubmit)} noValidate>
          <div className="form-grid form-grid--two-col">
            <Field
              label="Nome completo"
              autoComplete="name"
              error={errors.nome?.message}
              required
              {...register('nome')}
            />
            <Field label="E-mail" value={user.email} disabled hint="O e-mail é imutável." />
            <Select
              label="Papel"
              options={ROLE_OPTIONS}
              error={errors.papel?.message}
              disabled={isSelf}
              hint={isSelf ? 'Você não pode mudar seu próprio papel.' : undefined}
              required
              {...register('papel')}
            />
            <div className="form-grid__full">
              <Switch
                label="Ativo"
                hint={isSelf ? 'Você não pode se desativar.' : 'Desativar mata todas as sessões.'}
                disabled={isSelf}
                {...register('ativo')}
              />
            </div>
          </div>

          <div className="form-actions">
            <Button type="submit" variant="primary" loading={isSubmitting} disabled={!isDirty}>
              Salvar alterações
            </Button>
          </div>
        </form>
      </section>

      <section className="form-card form-card--danger">
        <h2 className="form-card__title">Ações sensíveis</h2>
        <p className="form-card__sub">
          Use o reset de senha quando o usuário esquecer a senha ou houver suspeita de
          comprometimento. Desativar é o passo final — preserva audit log mas bloqueia acesso.
        </p>
        <div className="form-actions form-actions--gap">
          <Button variant="secondary" onClick={() => setResetOpen(true)}>
            Redefinir senha
          </Button>
          <Button
            variant="danger"
            onClick={() => setDeactivateOpen(true)}
            disabled={isSelf || !user.ativo}
          >
            {user.ativo ? 'Desativar usuário' : 'Já desativado'}
          </Button>
        </div>
      </section>

      <Link to="/users">
        <Button variant="ghost">← Voltar à lista</Button>
      </Link>

      <UserResetPasswordDialog
        open={resetOpen}
        userId={user.id}
        userEmail={user.email}
        onClose={() => setResetOpen(false)}
      />

      <ConfirmDialog
        open={deactivateOpen}
        onClose={() => setDeactivateOpen(false)}
        onConfirm={handleDeactivate}
        title="Desativar usuário?"
        description={`${user.nome} (${user.email}) perderá acesso imediatamente. Sessões ativas serão encerradas. A operação pode ser revertida ativando-o de volta.`}
        confirmLabel="Sim, desativar"
        variant="danger"
        loading={deactivate.isPending}
      />
    </div>
  );
}
